import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { appelerApi, corpsJson } from "../api/client";
import type { Production, TableauBord } from "../api/types";
import { utiliserSession } from "../auth/SessionContext";
import { OrigineBadge } from "../components/OrigineBadge";
import { formatDate, valeurListe } from "../utils/format";

function texte(objet: Record<string, unknown>, cle: string): string {
  const valeur = objet[cle];
  return typeof valeur === "string" ? valeur : "";
}

export function ProductionPage() {
  const { id } = useParams();
  const { session } = utiliserSession();
  const [production, definirProduction] = useState<Production | null>(null);
  const [tableauBord, definirTableauBord] = useState<TableauBord | null>(null);
  const [erreur, definirErreur] = useState("");
  const [messageAction, definirMessageAction] = useState("");
  const [ville, definirVille] = useState("");
  const [message, definirMessage] = useState("");
  const [saisonId, definirSaisonId] = useState("");

  const organisationStructure = useMemo(
    () => session?.organisations.find((organisation) => organisation.type === "STRUCTURE"),
    [session]
  );

  useEffect(() => {
    if (!id) return;
    appelerApi<Production>(`/productions/${id}`, {}, Boolean(session))
      .then(definirProduction)
      .catch((cause: Error) => definirErreur(cause.message));
  }, [id, session]);

  useEffect(() => {
    if (!organisationStructure) return;
    appelerApi<TableauBord>(
      `/structures/${organisationStructure.id}/tableau-de-bord`,
      {},
      true
    )
      .then(definirTableauBord)
      .catch(() => definirTableauBord(null));
  }, [organisationStructure]);

  async function ajouterAuxFavoris() {
    if (!production || !organisationStructure) return;
    definirMessageAction("");
    try {
      await appelerApi(
        "/favoris",
        {
          method: "POST",
          body: corpsJson({
            organisation_id: organisationStructure.id,
            production_id: production.id
          })
        },
        true
      );
      definirMessageAction("Production ajoutée à vos favoris.");
    } catch (cause) {
      definirMessageAction(cause instanceof Error ? cause.message : "Action impossible");
    }
  }

  async function contacter(evenement: FormEvent) {
    evenement.preventDefault();
    if (!production || !organisationStructure) return;
    definirMessageAction("");
    try {
      await appelerApi(
        `/productions/${production.id}/contacts`,
        {
          method: "POST",
          body: corpsJson({
            organisation_id: organisationStructure.id,
            nombre_representations: 1,
            ville,
            message,
            coordonnees: {}
          })
        },
        true
      );
      definirMessageAction("Votre demande a été transmise au producteur.");
      definirMessage("");
    } catch (cause) {
      definirMessageAction(cause instanceof Error ? cause.message : "Envoi impossible");
    }
  }

  async function importerDansSaison() {
    if (!production || !saisonId) return;
    definirMessageAction("");
    try {
      await appelerApi(
        `/saisons/${saisonId}/importer-production`,
        {
          method: "POST",
          body: corpsJson({
            production_id: production.id,
            dates: [],
            tarifs: [],
            donnees_locales: {}
          })
        },
        true
      );
      definirMessageAction("La fiche a été copiée dans la saison, sans écraser vos données locales.");
    } catch (cause) {
      definirMessageAction(cause instanceof Error ? cause.message : "Import impossible");
    }
  }

  if (erreur) {
    return (
      <section className="page">
        <div className="etat-vide">
          <strong>Cette production n’est pas accessible.</strong>
          <p>{erreur}</p>
          <Link to="/catalogue">Retour au catalogue</Link>
        </div>
      </section>
    );
  }
  if (!production) {
    return <div className="chargement-page">Chargement de la fiche…</div>;
  }

  const disciplines = valeurListe(production.classification, "disciplines");
  const publics = valeurListe(production.classification, "publics");
  const image = production.medias[0];
  const presentationComplete =
    texte(production.presentation, "presentation_complete") ||
    texte(production.presentation, "description");

  return (
    <article className="fiche-production">
      <header className="fiche-production__hero">
        <div className="fiche-production__image">
          {image ? (
            <img src={image.url} alt={`Visuel de ${production.titre}`} />
          ) : (
            <span aria-hidden="true">{production.titre.slice(0, 2).toUpperCase()}</span>
          )}
        </div>
        <div className="fiche-production__titre">
          <Link className="retour" to="/catalogue">← Retour au catalogue</Link>
          <OrigineBadge origine={production.origine} />
          <h1>{production.titre}</h1>
          {production.sous_titre && <p className="sous-titre">{production.sous_titre}</p>}
          <p className="compagnie">{production.compagnie}</p>
          <div className="etiquettes etiquettes--larges">
            {[...disciplines, ...publics].slice(0, 6).map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
          <div className="fiche-production__actions">
            <a
              className="bouton bouton--clair"
              href={`/api/v1/productions/${production.id}/fiche.pdf`}
              download
            >
              Télécharger la fiche PDF
            </a>
            {organisationStructure && (
              <button className="bouton bouton--primaire" type="button" onClick={ajouterAuxFavoris}>
                Ajouter aux favoris
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="fiche-production__corps">
        <div className="fiche-production__principal">
          <section>
            <span className="sur-titre sur-titre--sombre">Le spectacle</span>
            <h2>{production.resume_court}</h2>
            {presentationComplete && <p className="texte-long">{presentationComplete}</p>}
          </section>

          <section>
            <div className="titre-section">
              <div>
                <span className="sur-titre sur-titre--sombre">En tournée</span>
                <h2>Prochaines disponibilités</h2>
              </div>
            </div>
            {production.disponibilites.length ? (
              <div className="liste-disponibilites">
                {production.disponibilites.map((disponibilite) => (
                  <article key={disponibilite.id}>
                    <strong>
                      Du {formatDate(disponibilite.date_debut)} au{" "}
                      {formatDate(disponibilite.date_fin)}
                    </strong>
                    <span>{disponibilite.statut.replaceAll("_", " ")}</span>
                    <p>{disponibilite.zones.join(" · ") || "Toutes zones"}</p>
                    <OrigineBadge origine={disponibilite.origine} />
                  </article>
                ))}
              </div>
            ) : (
              <p className="note">Les disponibilités seront bientôt renseignées.</p>
            )}
          </section>

          <section className="bloc-resultats">
            <div>
              <span className="sur-titre">Résultats contextualisés</span>
              <h2>Ce que les données racontent</h2>
              <p>
                Les statistiques publiques comparent uniquement des salles et
                événements cohérents. Les recettes détaillées restent privées.
              </p>
            </div>
            <Link className="bouton bouton--clair" to={`/productions/${production.id}/resultats`}>
              Consulter les résultats
            </Link>
          </section>
        </div>

        <aside className="fiche-production__aside">
          {organisationStructure ? (
            <>
              <section className="panneau-action">
                <span className="sur-titre sur-titre--sombre">Mise en relation directe</span>
                <h2>Contacter le producteur</h2>
                <form onSubmit={contacter}>
                  <label className="champ">
                    <span>Ville envisagée</span>
                    <input
                      required
                      minLength={2}
                      value={ville}
                      onChange={(e) => definirVille(e.target.value)}
                    />
                  </label>
                  <label className="champ">
                    <span>Votre message</span>
                    <textarea
                      required
                      minLength={20}
                      rows={5}
                      value={message}
                      onChange={(e) => definirMessage(e.target.value)}
                    />
                  </label>
                  <button className="bouton bouton--primaire" type="submit">
                    Envoyer la demande
                  </button>
                </form>
              </section>
              <section className="panneau-action">
                <span className="sur-titre sur-titre--sombre">Sans ressaisie</span>
                <h2>Importer dans une saison</h2>
                {tableauBord?.saisons?.length ? (
                  <>
                    <label className="champ">
                      <span>Saison</span>
                      <select value={saisonId} onChange={(e) => definirSaisonId(e.target.value)}>
                        <option value="">Choisir une saison</option>
                        {tableauBord.saisons.map((saison) => (
                          <option key={saison.id} value={saison.id}>
                            {saison.nom}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      className="bouton bouton--secondaire"
                      type="button"
                      disabled={!saisonId}
                      onClick={importerDansSaison}
                    >
                      Importer la fiche
                    </button>
                  </>
                ) : (
                  <p>Créez d’abord une saison depuis votre espace structure.</p>
                )}
              </section>
            </>
          ) : (
            <section className="panneau-action">
              <span className="sur-titre sur-titre--sombre">Vous programmez ?</span>
              <h2>Enregistrez et contactez en quelques minutes.</h2>
              <p>Créez gratuitement votre espace structure pour préparer votre saison.</p>
              <Link className="bouton bouton--primaire" to="/inscription">
                Créer un espace
              </Link>
            </section>
          )}
          {messageAction && <p className="alerte alerte--succes">{messageAction}</p>}
        </aside>
      </div>
    </article>
  );
}
